import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import type { SortingState } from '@tanstack/react-table';
import { ArrowUpDown, Columns3, Edit3, Play, SlidersHorizontal } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { isUsMarketHoursUtc } from 'shared';

import type { PositionIndicatorHistoryState } from '../../features/positions/queries';
import type { PositionDto, PositionStatusFlag } from '../../lib/api/endpoints';
import { formatCurrency } from '../../lib/format/currency';
import { relativeTimestamp } from '../../lib/format/date';
import { positionReplayHref } from '../../lib/navigation/positionReplay';
import { cn } from '../../lib/utils';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { Table } from '../ui/Table';
import { TableCell } from '../ui/TableCell';
import { TableHeaderCell } from '../ui/TableHeaderCell';

import { AtrExtensionValue } from './AtrExtensionValue';
import { EditStopDialog } from './EditStopDialog';
import { HistoricalPnlCaveat } from './HistoricalPnlCaveat';
import { PositionCard } from './PositionCard';
import { PositionSideBadge } from './PositionSideBadge';
import { PositionStatusPills } from './PositionStatusPills';
import { RebuyHigherBadge } from './RebuyHigherBadge';

interface PositionsTableProps {
  positions: PositionDto[];
  indicatorHistoryBySymbol?: Readonly<Record<string, PositionIndicatorHistoryState>>;
  historyIncomplete?: PositionDto[];
  onOpenDetail: (symbol: string) => void;
  onEditPosition: (position: PositionDto) => void;
  hasSnapshot?: boolean;
}

const columnHelper = createColumnHelper<PositionDto>();

function numberValue(value: string | null): number {
  return value === null ? Number.NEGATIVE_INFINITY : Number(value);
}

function percent(value: string | null): string {
  if (value === null) return '-';
  return `${(Number(value) * 100).toFixed(1)}%`;
}

function stopMetric(position: PositionDto, value: string | null, formatted: string): string {
  if (position.statusFlags.includes('INVALID_STOP')) return 'INVALID';
  return value === null ? '-' : formatted;
}

function distanceTone(value: string | null): 'danger' | 'neutral' | 'success' | 'warning' {
  if (value === null) return 'neutral';
  const pct = Number(value);
  if (pct > 0.05) return 'success';
  if (pct >= 0.02) return 'warning';
  return 'danger';
}

function isHistoryIncomplete(position: PositionDto): boolean {
  return position.historyIncomplete === true || position.statusFlags.includes('DATA_INCOMPLETE');
}

function quoteAgeMinutes(lastQuoteAt: string | null): number | null {
  if (!lastQuoteAt) return null;
  const quotedAt = new Date(lastQuoteAt).getTime();
  if (!Number.isFinite(quotedAt)) return null;
  return Math.max(0, Math.floor((Date.now() - quotedAt) / 60_000));
}

function priceSourceLabel(source: PositionDto['priceSource']): string {
  if (source === 'last-fill') return 'last fill';
  return source;
}

function pnlTone(value: number | string | null): 'danger' | 'neutral' | 'success' {
  if (value === null || Number(value) === 0) return 'neutral';
  const num = Number(value);
  return num > 0 ? 'success' : 'danger';
}

function formatPnlPercent(value: string | null): string {
  if (value === null) return '—';
  const num = Number(value);
  return `${num >= 0 ? '+' : ''}${num.toFixed(2)}%`;
}

function formatRMultiple(value: string | null): string {
  if (value === null) return '—';
  const num = Number(value);
  return `${num >= 0 ? '+' : ''}${num.toFixed(2)}R`;
}

function LifetimePnlCell({ position, value }: { position: PositionDto; value: number | null }) {
  return (
    <span className="flex flex-col items-end gap-1">
      <Badge tone={pnlTone(value)}>{formatCurrency(value, position.currency)}</Badge>
      {position.historicalPnlIncomplete ? <HistoricalPnlCaveat /> : null}
    </span>
  );
}

const STORAGE_KEY = 'dt:positions:visibleColumns';
const COLUMN_ORDER = [
  'symbol',
  'qty',
  'pctNav',
  'currentStop',
  'distanceToStop',
  'dollarRiskToStop',
  'unrealizedPnlDollar',
  'statusFlags',
  'actions',
  'marketValue',
  'avgCost',
  'currentPrice',
  'atrExtension',
  'daysHeld',
  'unrealizedPnlPct',
  'rMultiple',
  'realisedPnl',
  'totalPnl',
  'numberOfAdds',
  'numberOfPartialExits',
];
const NUMERIC_COLUMN_IDS = new Set([
  'qty',
  'marketValue',
  'pctNav',
  'avgCost',
  'currentPrice',
  'atrExtension',
  'currentStop',
  'distanceToStop',
  'dollarRiskToStop',
  'daysHeld',
  'unrealizedPnlDollar',
  'unrealizedPnlPct',
  'rMultiple',
  'realisedPnl',
  'totalPnl',
  'numberOfAdds',
  'numberOfPartialExits',
]);
const DEFAULT_VISIBLE_COLUMNS = [
  'qty',
  'pctNav',
  'distanceToStop',
  'dollarRiskToStop',
  'unrealizedPnlDollar',
];

function loadVisibleColumns(): Set<string> {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as unknown;
      if (Array.isArray(parsed)) {
        return new Set(parsed.filter((item): item is string => typeof item === 'string'));
      }
    }
  } catch {
    // Ignore parse errors
  }
  return new Set(DEFAULT_VISIBLE_COLUMNS);
}

function saveVisibleColumns(columns: Set<string>): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify([...columns]));
  } catch {
    // Ignore storage errors
  }
}

function LastPriceCell({ position }: { position: PositionDto }) {
  if (position.shortProvenance === 'AMBIGUOUS_PREHISTORY') {
    return <span className="text-warn">Valuation unavailable</span>;
  }

  const priceTimestamp = position.priceFetchedAt ?? position.lastQuoteAt;
  const ageMinutes = quoteAgeMinutes(priceTimestamp);
  const isStale =
    position.priceStale ||
    (ageMinutes !== null && ageMinutes > 30 && isUsMarketHoursUtc(new Date()));

  return (
    <span className="flex flex-col tabular-nums">
      <span>{formatCurrency(position.currentPrice, position.currency)}</span>
      <span
        className={cn(
          'text-xs',
          isStale || position.priceSource === 'last-fill' ? 'text-warn' : 'text-text-muted',
        )}
      >
        {priceSourceLabel(position.priceSource)}
        {isStale ? ' stale' : ''}
      </span>
      {priceTimestamp ? (
        <span className={cn('text-xs text-text-muted', isStale && 'text-warn')}>
          {relativeTimestamp(priceTimestamp)}
        </span>
      ) : null}
    </span>
  );
}

export function PositionsTable({
  hasSnapshot = true,
  historyIncomplete = [],
  onEditPosition,
  onOpenDetail,
  indicatorHistoryBySymbol = {},
  positions,
}: PositionsTableProps) {
  const [sorting, setSorting] = useState<SortingState>([{ id: 'pctNav', desc: true }]);
  const [themeFilter, setThemeFilter] = useState('ALL');
  const [statusFilter, setStatusFilter] = useState<'ALL' | PositionStatusFlag>('ALL');
  const [showLifecycleColumns, setShowLifecycleColumns] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState<Set<string>>(() => loadVisibleColumns());
  const [showColumnSelector, setShowColumnSelector] = useState(false);
  const columnSelectorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (columnSelectorRef.current && !columnSelectorRef.current.contains(event.target as Node)) {
        setShowColumnSelector(false);
      }
    };
    if (showColumnSelector) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
    return undefined;
  }, [showColumnSelector]);

  const toggleColumn = (columnId: string) => {
    setVisibleColumns((prev) => {
      const next = new Set(prev);
      if (next.has(columnId)) {
        next.delete(columnId);
      } else {
        next.add(columnId);
      }
      saveVisibleColumns(next);
      return next;
    });
  };

  const noStopCount = useMemo(
    () =>
      [...positions, ...historyIncomplete].filter((position) =>
        position.statusFlags.includes('NO_STOP'),
      ).length,
    [historyIncomplete, positions],
  );
  const themes = useMemo(
    () =>
      [
        ...new Set(
          [...positions, ...historyIncomplete].map((position) => position.theme).filter(Boolean),
        ),
      ].sort(),
    [historyIncomplete, positions],
  );
  const filteredPositions = useMemo(
    () =>
      positions.filter((position) => {
        const themeMatches = themeFilter === 'ALL' || position.theme === themeFilter;
        const statusMatches = statusFilter === 'ALL' || position.statusFlags.includes(statusFilter);
        return themeMatches && statusMatches;
      }),
    [positions, statusFilter, themeFilter],
  );
  const realPositions = useMemo(
    () => filteredPositions.filter((position) => !isHistoryIncomplete(position)),
    [filteredPositions],
  );
  const incompletePositions = useMemo(
    () =>
      [...filteredPositions.filter(isHistoryIncomplete), ...historyIncomplete].filter(
        (position) => {
          const themeMatches = themeFilter === 'ALL' || position.theme === themeFilter;
          const statusMatches =
            statusFilter === 'ALL' || position.statusFlags.includes(statusFilter);
          return themeMatches && statusMatches;
        },
      ),
    [filteredPositions, historyIncomplete, statusFilter, themeFilter],
  );
  const columns = useMemo(
    () => [
      columnHelper.accessor('symbol', {
        header: 'Symbol',
        cell: (info) => (
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="font-semibold text-primary hover:text-primary-hover"
              onClick={() => onOpenDetail(info.row.original.symbol)}
            >
              {info.getValue()}
            </button>
            <PositionSideBadge
              shortProvenance={info.row.original.shortProvenance}
              side={info.row.original.side}
            />
            {info.row.original.rebuyHigher ? (
              <RebuyHigherBadge
                currency={info.row.original.currency}
                flag={info.row.original.rebuyHigher}
              />
            ) : null}
          </div>
        ),
      }),
      columnHelper.accessor('qty', {
        header: 'Qty',
        sortingFn: (left, right) => Number(left.original.qty) - Number(right.original.qty),
        cell: (info) => <span className="tabular-nums">{info.getValue()}</span>,
      }),
      columnHelper.accessor('marketValue', {
        id: 'marketValue',
        header: 'MV',
        sortingFn: (left, right) =>
          Number(left.original.marketValue) - Number(right.original.marketValue),
        cell: (info) => (
          <span className="tabular-nums">
            {formatCurrency(info.getValue(), info.row.original.currency)}
          </span>
        ),
      }),
      columnHelper.accessor('pctNav', {
        id: 'pctNav',
        header: '%NAV',
        sortingFn: (left, right) => Number(left.original.pctNav) - Number(right.original.pctNav),
        cell: (info) =>
          hasSnapshot ? (
            <span className="tabular-nums">{percent(info.getValue())}</span>
          ) : (
            <span
              className="tabular-nums text-text-muted"
              title="Set your NAV in Settings to see position sizing."
            >
              —
            </span>
          ),
      }),
      columnHelper.accessor('avgCost', {
        header: 'Entry',
        sortingFn: (left, right) => Number(left.original.avgCost) - Number(right.original.avgCost),
        cell: (info) => (
          <span className="tabular-nums">
            {formatCurrency(info.getValue(), info.row.original.currency)}
          </span>
        ),
      }),
      columnHelper.accessor('currentPrice', {
        header: 'Last',
        sortingFn: (left, right) =>
          Number(left.original.currentPrice) - Number(right.original.currentPrice),
        cell: (info) => <LastPriceCell position={info.row.original} />,
      }),
      columnHelper.display({
        id: 'atrExtension',
        header: () => (
          <span title="Price distance from 50-day SMA, measured in ATR(14) multiples">ATR Ext</span>
        ),
        enableSorting: false,
        cell: (info) => (
          <AtrExtensionValue history={indicatorHistoryBySymbol[info.row.original.symbol]} />
        ),
      }),
      columnHelper.accessor('currentStop', {
        header: 'Stop',
        sortingFn: (left, right) =>
          numberValue(left.original.currentStop) - numberValue(right.original.currentStop),
        cell: (info) => (
          <div className="flex items-center gap-2">
            <span className="tabular-nums">
              {info.getValue() ? formatCurrency(info.getValue(), info.row.original.currency) : '-'}
            </span>
            <EditStopDialog
              position={info.row.original}
              trigger={
                <Button
                  aria-label={`Edit stop for ${info.row.original.symbol}`}
                  className="h-8 w-8 px-0"
                  size="sm"
                  variant="ghost"
                >
                  <Edit3 className="h-4 w-4" aria-hidden="true" />
                </Button>
              }
            />
          </div>
        ),
      }),
      columnHelper.accessor('distanceToStop', {
        header: 'Distance',
        sortingFn: (left, right) =>
          numberValue(left.original.distanceToStop) - numberValue(right.original.distanceToStop),
        cell: (info) => {
          const invalidStop = info.row.original.statusFlags.includes('INVALID_STOP');
          return (
            <Badge tone={invalidStop ? 'danger' : distanceTone(info.getValue())}>
              {stopMetric(info.row.original, info.getValue(), percent(info.getValue()))}
            </Badge>
          );
        },
      }),
      columnHelper.accessor('dollarRiskToStop', {
        header: '$Risk',
        sortingFn: (left, right) =>
          numberValue(left.original.dollarRiskToStop) -
          numberValue(right.original.dollarRiskToStop),
        cell: (info) =>
          info.row.original.statusFlags.includes('INVALID_STOP') ? (
            <Badge tone="danger">Invalid</Badge>
          ) : (
            <span className="tabular-nums">
              {stopMetric(
                info.row.original,
                info.getValue(),
                formatCurrency(info.getValue(), info.row.original.currency),
              )}
            </span>
          ),
      }),
      columnHelper.accessor('daysHeld', {
        header: 'Days',
        cell: (info) => <span className="tabular-nums">{info.getValue()}</span>,
      }),
      columnHelper.accessor('unrealizedPnlDollar', {
        id: 'unrealizedPnlDollar',
        header: 'Unreal. P&L',
        sortingFn: (left, right) =>
          numberValue(left.original.unrealizedPnlDollar) -
          numberValue(right.original.unrealizedPnlDollar),
        cell: (info) => (
          <Badge tone={pnlTone(info.getValue())}>
            {info.getValue() ? formatCurrency(info.getValue(), info.row.original.currency) : '—'}
          </Badge>
        ),
      }),
      columnHelper.accessor('unrealizedPnlPct', {
        id: 'unrealizedPnlPct',
        header: 'Unreal. P&L %',
        sortingFn: (left, right) =>
          numberValue(left.original.unrealizedPnlPct) -
          numberValue(right.original.unrealizedPnlPct),
        cell: (info) => (
          <Badge tone={pnlTone(info.getValue())}>{formatPnlPercent(info.getValue())}</Badge>
        ),
      }),
      columnHelper.accessor('rMultiple', {
        id: 'rMultiple',
        header: 'R Multiple',
        sortingFn: (left, right) =>
          numberValue(left.original.rMultiple) - numberValue(right.original.rMultiple),
        cell: (info) => {
          const position = info.row.original;
          const rMultiple = info.getValue();
          const isProtected =
            rMultiple &&
            Number(rMultiple) > 0 &&
            position.currentStop &&
            Number(position.currentStop) > Number(position.avgCost);
          const displayValue = isProtected
            ? `🔒 ${formatRMultiple(rMultiple)}`
            : formatRMultiple(rMultiple);
          return <Badge tone={pnlTone(rMultiple)}>{displayValue}</Badge>;
        },
      }),
      columnHelper.accessor('realisedPnl', {
        header: 'R.PnL',
        sortingFn: (left, right) =>
          (left.original.realisedPnl ?? Number.NEGATIVE_INFINITY) -
          (right.original.realisedPnl ?? Number.NEGATIVE_INFINITY),
        cell: (info) => <LifetimePnlCell position={info.row.original} value={info.getValue()} />,
      }),
      columnHelper.accessor('totalPnl', {
        header: 'T.PnL',
        sortingFn: (left, right) =>
          (left.original.totalPnl ?? Number.NEGATIVE_INFINITY) -
          (right.original.totalPnl ?? Number.NEGATIVE_INFINITY),
        cell: (info) => <LifetimePnlCell position={info.row.original} value={info.getValue()} />,
      }),
      columnHelper.accessor('numberOfAdds', {
        header: 'Adds',
        cell: (info) => <span className="tabular-nums">{info.getValue()}</span>,
      }),
      columnHelper.accessor('numberOfPartialExits', {
        header: 'Exits',
        cell: (info) => <span className="tabular-nums">{info.getValue()}</span>,
      }),
      columnHelper.accessor('statusFlags', {
        header: 'Status',
        enableSorting: false,
        cell: (info) => <PositionStatusPills flags={info.getValue()} />,
      }),
      columnHelper.display({
        id: 'actions',
        header: '',
        cell: (info) => (
          <div className="flex items-center gap-1">
            <Button asChild className="h-8 w-8 px-0" size="sm" variant="ghost">
              <a
                aria-label={`Replay trades for ${info.row.original.symbol}`}
                href={positionReplayHref(info.row.original.symbol, info.row.original.accountId)}
              >
                <Play className="h-4 w-4" aria-hidden="true" />
              </a>
            </Button>
            <Button
              aria-label={`Edit position override for ${info.row.original.symbol}`}
              className="h-8 w-8 px-0"
              size="sm"
              type="button"
              variant="ghost"
              onClick={() => onEditPosition(info.row.original)}
            >
              <Edit3 className="h-4 w-4" aria-hidden="true" />
            </Button>
          </div>
        ),
      }),
    ],
    [hasSnapshot, indicatorHistoryBySymbol, onEditPosition, onOpenDetail],
  );
  const table = useReactTable({
    data: realPositions,
    columns,
    state: { columnOrder: COLUMN_ORDER, sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });
  const lifecycleColumnIds = useMemo(
    () => new Set(['realisedPnl', 'totalPnl', 'numberOfAdds', 'numberOfPartialExits']),
    [],
  );

  const optionalColumns = useMemo(
    () => [
      { id: 'qty', label: 'Qty' },
      { id: 'marketValue', label: 'MV' },
      { id: 'pctNav', label: '%NAV' },
      { id: 'avgCost', label: 'Entry' },
      { id: 'currentPrice', label: 'Last' },
      { id: 'atrExtension', label: 'ATR Ext' },
      { id: 'distanceToStop', label: 'Distance' },
      { id: 'dollarRiskToStop', label: '$Risk' },
      { id: 'daysHeld', label: 'Days' },
      { id: 'unrealizedPnlDollar', label: 'Unreal. P&L' },
      { id: 'unrealizedPnlPct', label: 'Unreal. P&L %' },
      { id: 'rMultiple', label: 'R Multiple' },
    ],
    [],
  );

  const isColumnVisible = (columnId: string): boolean => {
    if (lifecycleColumnIds.has(columnId)) {
      return showLifecycleColumns;
    }
    const isOptional = optionalColumns.some((col) => col.id === columnId);
    return !isOptional || visibleColumns.has(columnId);
  };

  const portfolioSummary = useMemo(() => {
    if (realPositions.length === 0) return null;

    const mvValues = realPositions
      .map((p) => (p.marketValue ? Number(p.marketValue) : null))
      .filter((v): v is number => v !== null);
    const pctNavValues = realPositions
      .map((p) => (p.pctNav ? Number(p.pctNav) : null))
      .filter((v): v is number => v !== null);
    const unrealizedPnlDollarValues = realPositions
      .map((p) => (p.unrealizedPnlDollar ? Number(p.unrealizedPnlDollar) : null))
      .filter((v): v is number => v !== null);
    const riskValues = realPositions
      .map((p) => (p.dollarRiskToStop ? Number(p.dollarRiskToStop) : null))
      .filter((v): v is number => v !== null);
    const rMultipleValues = realPositions
      .map((p) => (p.rMultiple ? Number(p.rMultiple) : null))
      .filter((v): v is number => v !== null);

    const totalMV = mvValues.length > 0 ? mvValues.reduce((sum, v) => sum + v, 0) : null;
    const totalPctNav =
      pctNavValues.length > 0 ? pctNavValues.reduce((sum, v) => sum + v, 0) : null;
    const totalUnrealizedPnlDollar =
      unrealizedPnlDollarValues.length > 0
        ? unrealizedPnlDollarValues.reduce((sum, v) => sum + v, 0)
        : null;
    const totalRisk = riskValues.length > 0 ? riskValues.reduce((sum, v) => sum + v, 0) : null;
    const avgRMultiple =
      rMultipleValues.length > 0
        ? rMultipleValues.reduce((sum, v) => sum + v, 0) / rMultipleValues.length
        : null;

    // Weighted average unrealizedPnlPct by MV
    const weightsAndPnlPcts = realPositions
      .map((p) => {
        const mv = p.marketValue ? Number(p.marketValue) : null;
        const pnlPct = p.unrealizedPnlPct ? Number(p.unrealizedPnlPct) : null;
        return mv !== null && pnlPct !== null ? { weight: mv, value: pnlPct } : null;
      })
      .filter((v): v is { weight: number; value: number } => v !== null);

    const weightedAvgUnrealizedPnlPct =
      weightsAndPnlPcts.length > 0
        ? weightsAndPnlPcts.reduce((sum, item) => sum + item.weight * item.value, 0) /
          weightsAndPnlPcts.reduce((sum, item) => sum + item.weight, 0)
        : null;

    // Assume USD for aggregation (all positions should have currency, but default to USD)
    const currency = realPositions[0]?.currency ?? 'USD';

    return {
      totalMV,
      totalPctNav,
      totalUnrealizedPnlDollar,
      weightedAvgUnrealizedPnlPct,
      totalRisk,
      avgRMultiple,
      currency,
    };
  }, [realPositions]);

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <Button
          aria-label="Filter positions with no stop"
          className="self-start sm:self-end"
          type="button"
          variant={statusFilter === 'NO_STOP' ? 'primary' : 'secondary'}
          onClick={() => setStatusFilter(statusFilter === 'NO_STOP' ? 'ALL' : 'NO_STOP')}
        >
          No stop ({noStopCount})
        </Button>
        <Button
          aria-label="Toggle lifecycle columns"
          className="self-start sm:self-end"
          type="button"
          variant={showLifecycleColumns ? 'primary' : 'secondary'}
          onClick={() => setShowLifecycleColumns((visible) => !visible)}
        >
          <SlidersHorizontal className="h-4 w-4" aria-hidden="true" />
          Lifecycle
        </Button>
        <div className="relative self-start sm:self-end" ref={columnSelectorRef}>
          <Button
            aria-label="Select visible columns"
            type="button"
            variant="secondary"
            onClick={() => setShowColumnSelector((visible) => !visible)}
          >
            <Columns3 className="h-4 w-4" aria-hidden="true" />
            Columns
          </Button>
          {showColumnSelector ? (
            <div className="absolute left-0 top-full z-10 mt-2 w-56 rounded border border-border bg-surface shadow-lg">
              <div className="p-3 text-sm">
                <div className="mb-2 font-semibold text-text">Optional Columns</div>
                <div className="space-y-2">
                  {optionalColumns.map((col) => (
                    <label key={col.id} className="flex cursor-pointer items-center gap-2">
                      <input
                        checked={visibleColumns.has(col.id)}
                        className="h-4 w-4 rounded border-border bg-bg-muted text-primary accent-primary"
                        type="checkbox"
                        onChange={() => toggleColumn(col.id)}
                      />
                      <span className="text-text">{col.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          ) : null}
        </div>
        <label className="text-sm text-text-muted">
          Theme
          <select
            className="mt-1 block w-full rounded border border-border bg-bg-muted px-3 py-2 text-text sm:w-44"
            value={themeFilter}
            onChange={(event) => setThemeFilter(event.target.value)}
          >
            <option value="ALL">All themes</option>
            {themes.map((theme) => (
              <option key={theme} value={theme ?? ''}>
                {theme}
              </option>
            ))}
          </select>
        </label>
        <label className="text-sm text-text-muted">
          Status
          <select
            className="mt-1 block w-full rounded border border-border bg-bg-muted px-3 py-2 text-text sm:w-56"
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value as 'ALL' | PositionStatusFlag)}
          >
            <option value="ALL">All statuses</option>
            <option value="NO_STOP">No stop</option>
            <option value="INVALID_STOP">Invalid stop</option>
            <option value="DATA_INCOMPLETE">History incomplete</option>
            <option value="STOP_MOVED_ADVERSE">Stop moved</option>
            <option value="HELD_PAST_PLAN">Past plan</option>
            <option value="OVERSIZED">Oversized</option>
            <option value="THEME_OVER_CAP">Theme cap</option>
            <option value="MANUAL_OVERRIDE">Manual</option>
          </select>
        </label>
      </div>

      <div className="grid gap-3 sm:hidden">
        {realPositions.map((position) => (
          <PositionCard
            key={position.symbol}
            position={position}
            indicatorHistory={indicatorHistoryBySymbol[position.symbol]}
            onEditPosition={onEditPosition}
            onOpenDetail={onOpenDetail}
          />
        ))}
      </div>

      <div className="hidden overflow-x-auto rounded border border-border sm:block">
        <Table className="min-w-full divide-y divide-border">
          <thead className="bg-bg-muted text-left text-xs uppercase tracking-normal text-text-muted">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) =>
                  !isColumnVisible(header.column.id) ? null : (
                    <TableHeaderCell
                      key={header.id}
                      scope="col"
                      className="px-2 py-2"
                      numeric={NUMERIC_COLUMN_IDS.has(header.column.id)}
                    >
                      {header.isPlaceholder ? null : (
                        <button
                          type="button"
                          className="inline-flex items-center gap-1 font-semibold"
                          disabled={!header.column.getCanSort()}
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {header.column.getCanSort() ? (
                            <ArrowUpDown className="h-3.5 w-3.5" aria-hidden="true" />
                          ) : null}
                        </button>
                      )}
                    </TableHeaderCell>
                  ),
                )}
              </tr>
            ))}
          </thead>
          <tbody className="divide-y divide-border bg-surface">
            {table.getRowModel().rows.map((row) => (
              <tr key={row.id} className="hover:bg-surface-raised">
                {row.getVisibleCells().map((cell) =>
                  !isColumnVisible(cell.column.id) ? null : (
                    <TableCell
                      key={cell.id}
                      className="whitespace-nowrap px-2 py-2 align-middle"
                      numeric={NUMERIC_COLUMN_IDS.has(cell.column.id)}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ),
                )}
              </tr>
            ))}
            {table.getRowModel().rows.length === 0 ? (
              <tr>
                <TableCell
                  colSpan={columns.length}
                  className="px-3 py-6 text-center text-text-muted"
                >
                  No complete positions match these filters.
                </TableCell>
              </tr>
            ) : null}
          </tbody>
          {portfolioSummary && table.getRowModel().rows.length > 0 ? (
            <tfoot className="bg-slate-700">
              <tr>
                {table.getFlatHeaders().map((header) => {
                  if (!isColumnVisible(header.column.id)) return null;
                  const colId = header.column.id;

                  let content: React.ReactNode = null;

                  if (colId === 'symbol') {
                    content = <span className="font-semibold text-text-muted">Portfolio</span>;
                  } else if (colId === 'marketValue') {
                    content = (
                      <span className="font-semibold tabular-nums">
                        {portfolioSummary.totalMV !== null
                          ? formatCurrency(
                              portfolioSummary.totalMV.toString(),
                              portfolioSummary.currency,
                            )
                          : '—'}
                      </span>
                    );
                  } else if (colId === 'pctNav') {
                    content = <span className="font-semibold tabular-nums">{'—'}</span>;
                  } else if (colId === 'unrealizedPnlDollar') {
                    content = (
                      <Badge tone={pnlTone(portfolioSummary.totalUnrealizedPnlDollar)}>
                        {portfolioSummary.totalUnrealizedPnlDollar !== null
                          ? formatCurrency(
                              portfolioSummary.totalUnrealizedPnlDollar.toString(),
                              portfolioSummary.currency,
                            )
                          : '—'}
                      </Badge>
                    );
                  } else if (colId === 'unrealizedPnlPct') {
                    content = (
                      <Badge tone={pnlTone(portfolioSummary.weightedAvgUnrealizedPnlPct)}>
                        {portfolioSummary.weightedAvgUnrealizedPnlPct !== null
                          ? `${
                              portfolioSummary.weightedAvgUnrealizedPnlPct >= 0 ? '+' : ''
                            }${portfolioSummary.weightedAvgUnrealizedPnlPct.toFixed(2)}%`
                          : '—'}
                      </Badge>
                    );
                  } else if (colId === 'dollarRiskToStop') {
                    content = (
                      <span className="font-semibold tabular-nums">
                        {portfolioSummary.totalRisk !== null
                          ? formatCurrency(
                              portfolioSummary.totalRisk.toString(),
                              portfolioSummary.currency,
                            )
                          : '—'}
                      </span>
                    );
                  } else if (colId === 'rMultiple') {
                    content = (
                      <span className="font-semibold tabular-nums">
                        {portfolioSummary.avgRMultiple !== null
                          ? `${portfolioSummary.avgRMultiple >= 0 ? '+' : ''}${portfolioSummary.avgRMultiple.toFixed(1)}R avg`
                          : '—'}
                      </span>
                    );
                  }

                  return (
                    <TableCell
                      key={header.id}
                      className="whitespace-nowrap px-2 py-2 align-middle"
                      numeric={NUMERIC_COLUMN_IDS.has(colId)}
                    >
                      {content}
                    </TableCell>
                  );
                })}
              </tr>
            </tfoot>
          ) : null}
        </Table>
      </div>

      {incompletePositions.length > 0 ? (
        <section className="space-y-3 rounded border border-warn/60 bg-warn-subtle p-4">
          <div>
            <h2 className="text-sm font-semibold text-amber-100">History incomplete</h2>
            <p className="mt-1 text-sm text-amber-100/90">
              These positions have incomplete trade history. They may have been opened before your
              earliest import.
            </p>
          </div>
          <div className="grid gap-3 sm:hidden">
            {incompletePositions.map((position) => (
              <PositionCard
                key={position.symbol}
                position={position}
                indicatorHistory={indicatorHistoryBySymbol[position.symbol]}
                onEditPosition={onEditPosition}
                onOpenDetail={onOpenDetail}
              />
            ))}
          </div>
          <div className="hidden overflow-x-auto rounded border border-warn/50 sm:block">
            <Table className="min-w-full divide-y divide-warn/40">
              <thead className="bg-warn-subtle text-left text-xs uppercase tracking-normal text-amber-100">
                <tr>
                  <TableHeaderCell scope="col">Symbol</TableHeaderCell>
                  <TableHeaderCell scope="col" numeric>
                    Qty
                  </TableHeaderCell>
                  <TableHeaderCell scope="col" numeric>
                    Last
                  </TableHeaderCell>
                  <TableHeaderCell
                    scope="col"
                    numeric
                    title="Price distance from 50-day SMA, measured in ATR(14) multiples"
                  >
                    ATR Ext
                  </TableHeaderCell>
                  <TableHeaderCell scope="col">Status</TableHeaderCell>
                  <TableHeaderCell scope="col">
                    <span className="sr-only">Actions</span>
                  </TableHeaderCell>
                </tr>
              </thead>
              <tbody className="divide-y divide-warn/30 bg-surface">
                {incompletePositions.map((position) => (
                  <tr key={position.symbol}>
                    <TableCell className="whitespace-nowrap align-middle">
                      <button
                        type="button"
                        className="font-semibold text-primary hover:text-primary-hover disabled:cursor-default disabled:text-amber-100"
                        disabled={position.shortProvenance === 'AMBIGUOUS_PREHISTORY'}
                        onClick={() => onOpenDetail(position.symbol)}
                      >
                        {position.symbol}
                      </button>
                      <PositionSideBadge
                        shortProvenance={position.shortProvenance}
                        side={position.side}
                      />
                    </TableCell>
                    <TableCell className="whitespace-nowrap align-middle" numeric>
                      {position.qty}
                    </TableCell>
                    <TableCell className="whitespace-nowrap align-middle" numeric>
                      <LastPriceCell position={position} />
                    </TableCell>
                    <TableCell className="whitespace-nowrap align-middle" numeric>
                      <AtrExtensionValue history={indicatorHistoryBySymbol[position.symbol]} />
                    </TableCell>
                    <TableCell className="whitespace-nowrap align-middle">
                      <PositionStatusPills flags={position.statusFlags} />
                    </TableCell>
                    <TableCell className="whitespace-nowrap align-middle">
                      <div className="flex items-center gap-1">
                        <Button asChild className="h-8 w-8 px-0" size="sm" variant="ghost">
                          <a
                            aria-label={`Replay trades for ${position.symbol}`}
                            href={positionReplayHref(position.symbol, position.accountId)}
                          >
                            <Play className="h-4 w-4" aria-hidden="true" />
                          </a>
                        </Button>
                        <Button
                          aria-label={`Edit position override for ${position.symbol}`}
                          className="h-8 w-8 px-0"
                          size="sm"
                          type="button"
                          variant="ghost"
                          onClick={() => onEditPosition(position)}
                        >
                          <Edit3 className="h-4 w-4" aria-hidden="true" />
                        </Button>
                      </div>
                    </TableCell>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </section>
      ) : null}
    </div>
  );
}
